import {
  ICluster,
  IPoint,
  IPointProperties,
  IStacked
} from '../../../types-library/src'
import { presenter } from '@wiggot/core-apis/src/presenter'

import { listingsBasicApi } from '@wiggot/apis'
import { IHttpResponse } from '../types'

// El endpoint /properties/wiggot/tiles responde con el formato de la API
// nueva (priceAmount, media: [{ url }], bedrooms/bathrooms sueltos, address
// como un solo string), pero IPointProperties -que usan los marcadores y
// popups del mapa- sigue esperando el formato viejo (price, coverImage,
// numBedrooms/numBathrooms, addressStreet, etc.). Sin este mapeo los
// marcadores se veian sin precio ("$-" en el label) y el popup sin imagen
// ($0 MXN y el icono de "no se pudo cargar la imagen").
const mapRawPropertiesToPointProperties = (raw: any): IPointProperties => {
  const addressParts: string[] = (raw?.address || '').split(', ')

  return {
    ...raw,
    id: raw?.id,
    listingType: raw?.listingType || '',
    propertyType: raw?.propertyType,
    price: parseFloat(raw?.priceAmount) || 0,
    coverImage: raw?.media?.[0]?.url || '',
    numBedrooms: Number(raw?.bedrooms) || 0,
    numBathrooms: Number(raw?.bathrooms) || 0,
    numParkingLots: Number(raw?.parkings) || 0,
    surfaceTotal:
      parseFloat(raw?.constructionSurface) ||
      parseFloat(raw?.terrainSurface) ||
      0,
    addressStreet: addressParts[0] || raw?.address || '',
    addressNumber: '',
    addressNeighborhood: addressParts[1] || '',
    addressMunicipality: addressParts[2] || '',
    addressState: addressParts[3] || ''
  }
}

// Aplica el mapeo anterior a las properties de un feature: si es un punto
// "stacked" (varias propiedades en el mismo lugar), mapea cada elemento de
// `properties.stacked`; si es un listing individual, mapea `properties`
// directamente. Los features de cluster no se tocan (solo necesitan
// cluster/clusterId/pointCount, que ya vienen en el formato correcto).
const mapRawFeatureProperties = (item: any) => ({
  ...item,
  properties: item?.properties?.stacked?.length
    ? {
        ...item.properties,
        stacked: item.properties.stacked.map(mapRawPropertiesToPointProperties)
      }
    : mapRawPropertiesToPointProperties(item?.properties)
})

export const getPropertiesFromVectorTile = async (params: {
  queryParams: string
}): Promise<
  IHttpResponse<{
    points: IPoint[]
    clusters: ICluster[]
    stackeds: IStacked[]
  }>
> => {
  try {
    const { queryParams } = params

    // listingsBasicApi.get ya devuelve el body de la respuesta (no el
    // AxiosResponse completo), a diferencia del propertyBasicAPI que
    // usaba monopolio-ui.
    const data: any = await listingsBasicApi.get(
      `/properties/wiggot/tiles/${queryParams}`
    )

    if (data?.features?.length === 0) {
      return {
        isSuccess: false
      }
    }

    const clusters = data?.features?.filter(
      (item: { properties: { cluster: boolean } }) => item?.properties?.cluster
    )

    const clustersNomenclature = presenter<ICluster[]>({
      style: 'camel',
      value: clusters,
    })

    const listings = data?.features
      ?.filter(
        (item: { properties: { cluster: boolean; stacked: any[] } }) =>
          !item?.properties?.cluster && !item?.properties?.stacked?.length
      )
      ?.map(mapRawFeatureProperties)

    const listingsNomenclature = presenter<IPoint[]>({
      style: 'camel',
      value: listings,
    })

    const stackeds = data?.features
      ?.filter(
        (item: { properties: { cluster: boolean; stacked: any[] } }) =>
          !item?.properties?.cluster && item?.properties?.stacked?.length
      )
      ?.map(mapRawFeatureProperties)

    const stackedsNomenclature = presenter<IStacked[]>({
      style: 'camel',
      value: stackeds,
    })

    return {
      isSuccess: true,
      data: {
        clusters: clustersNomenclature,
        points: listingsNomenclature,
        stackeds: stackedsNomenclature,
      },
    }
  } catch (error: any) {
    return {
      isSuccess: false,
      error: {
        code: error?.response?.status,
        message: error?.response?.data?.message,
      },
    }
  }
}
